/**
 * shapeConverter.ts
 *
 * StorageShape(저장용) ↔ RenderNode(렌더링용) 양방향 변환 함수.
 *
 * 이 모듈의 책임은 **좌표 표현 변환**만이다:
 *   - 저장 포맷: `coordinates: [[timestamp, price], ...]`
 *   - 렌더 포맷: 도형별 전용 필드 (`start/end`, `startXY/endXY/endXY2`, `points`, `yValue`, `x1/y1/x2/y2`, ...)
 *
 * 속성 필드(border, line, background, levels, ...)는 StorageShape와 RenderNode가 1:1로
 * 공유하므로 별도 변환 없이 그대로 복사한다. 과거에 존재하던 `renderNodeToStorage`와
 * 14개의 `extract*` 헬퍼, `convertToAppearance` 호출은 모두 삭제되었다.
 *
 * - `storageToRenderNode`: StorageShape → RenderNode (load)
 * - `nodeToStorageShape`:  RenderNode → StorageShape (save, load 역방향)
 */

import type { StorageShapeInput } from '@/types/shape';
import { ShapeType } from '@/types/commontypes';
import { netRoeToPrice } from '@/utils/calculateSLndTP';
import type { RenderNode, RenderNodeMap, OrderLineRenderNode, OrderLineRenderStyle } from '@/types/commontypes';
import type { StockData } from '@/types/chart';
import type { XAccessor } from '@/hooks/use-candle-data';
import type { Coordinate } from '@/types/properties';
import { coordinatesToShape } from './coordinateConverter';
import { COLORS } from '@/constants/colors';

/**
 * ORDER_LINE 렌더링에 필요한 style 기본값.
 * InteractiveOrderLine.defaultProps.defaultPriceCoordinate에서 추출.
 * EachInteractiveOrderLine이 textBox, edge, stroke 등을 필수로 참조한다.
 */
export const ORDER_LINE_RENDER_DEFAULTS = {
  bgFill: '#FFFFFF',
  bgOpacity: 1,
  stroke: '#6574CD',
  strokeOpacity: 1,
  strokeDasharray: 'ShortDot',
  strokeWidth: 1,
  textFill: '#6574CD',
  fontFamily: "-apple-system, system-ui, Roboto, 'Helvetica Neue', Ubuntu, sans-serif",
  fontSize: 12,
  fontStyle: 'normal',
  fontWeight: 'normal',
  text: 'Alert',
  draggable: true,
  selected: false,
  textBox: {
    coordinateValue: { height: 24, right: 145, padding: { left: 10, right: 10 } },
    selectedType: { height: 24, right: 81, padding: { left: 10, right: 10 } },
    closeIcon: { height: 24, right: 60, padding: { left: 6, right: 6 } },
  },
  edge: {
    stroke: '#6574CD',
    strokeOpacity: 1,
    strokeWidth: 1,
    fill: '#FFFFFF',
    fillOpacity: 1,
    orient: 'right',
    at: 'right',
    arrowWidth: 10,
    dx: 0,
    rectWidth: 50,
    rectHeight: 20,
    r: 5,
  },
} as const satisfies OrderLineRenderStyle;

// ─── StorageShape → RenderNode ──────────────────────────────────────

/**
 * StorageShape(저장용) → RenderNode(렌더링용) 변환.
 *
 * - 속성 필드는 spread로 1:1 복사 (border, middle, line, levels, background, ...).
 * - `coordinates`는 `coordinatesToShape`로 도형별 좌표 mixin(start/end, startXY/endXY/endXY2,
 *   points, yValue, x1/y1/x2/y2, position 등)으로 분해된다.
 * - ORDER_LINE은 특수 케이스: `price → yValue` 매핑, 색상 재계산, 렌더링용 기본 스타일 주입.
 * - 이전 버전에서 호출하던 `convertToAppearance`는 제거 — RenderNode는 storage-shape 필드를
 *   그대로 보유한다.
 *
 * @example
 * const tl: TrendLineStorageShape = ...;
 * const node = storageToRenderNode(tl, ...); // ExtensibleInfoLineRenderNode
 */
export function storageToRenderNode<S extends StorageShapeInput>(
  shape: S,
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
): RenderNodeMap[S['type']];
export function storageToRenderNode(
  shape: StorageShapeInput,
  candleData: StockData[],
  timeframe: string,
  xAccessor?: XAccessor,
): RenderNode {
  // 좌표 변환: [timestamp, price][] → 도형별 좌표 mixin 필드
  const coordFields = coordinatesToShape(shape.coordinates, candleData, shape.type, timeframe, xAccessor);

  // hidden/locked는 사용자가 명시적으로 설정한 영구 상태이며 storage에 저장된다.
  // shape에 정의되지 않은 경우(기본값) runtime에서도 추가하지 않아 round-trip 정합성을 유지한다.
  // 렌더 경로의 `if (each.hidden)`/`if (each.locked)` 체크는 undefined를 falsy로 간주하므로 안전.
  const runtime: { hidden?: boolean; locked?: boolean } = {};
  if (shape.hidden !== undefined) runtime.hidden = shape.hidden;
  if (shape.locked !== undefined) runtime.locked = shape.locked;

  switch (shape.type) {
    case ShapeType.TREND_LINE:
    case ShapeType.RAY:
    case ShapeType.INFO_LINE: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.HORZ_LINE: {
      // coordinatesToShape가 yValue를 생성.

      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.VERT_LINE: {
      // coordinatesToShape가 xValue, start, end를 모두 생성.
      // candleData 없는 복원 시에는 timestamp 기반 임시값, ShapeManager recalculation에서 보정.

      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.PARALLEL_CHANNEL: {
      // dy = 3번째 좌표(앵커 포인트)의 y값 - 1번째 좌표의 y값
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const startXY = coordFields.startXY as [number, number] | undefined;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const endXY2 = coordFields.endXY2 as [number, number] | undefined;
      const dy = startXY && endXY2 ? endXY2[1] - startXY[1] : 0;

      return {
        ...shape,
        ...coordFields,
        dy,
        ...runtime,
      };
    }

    case ShapeType.TRIANGLE: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const startXY = coordFields.startXY as [number, number] | undefined;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const endXY2 = coordFields.endXY2 as [number, number] | undefined;
      const dy = startXY && endXY2 ? endXY2[1] - startXY[1] : 0;

      return {
        ...shape,
        ...coordFields,
        dy,
        ...runtime,
      };
    }

    case ShapeType.FIB_RETRACEMENT: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.FIB_CHANNEL: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.GANN_BOX: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.GANN_FAN: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.ELLIOTT_IMPULSE_12345: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.ELLIOTT_CORRECTION_ABC: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.XABCD_PATTERN: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.RECTANGLE: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.PATH: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.TEXT: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.FIXED_RANGE_VOLUME_PROFILE: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.ANCHORED_VOLUME_PROFILE: {
      return {
        ...shape,
        ...coordFields,
        ...runtime,
      };
    }

    case ShapeType.ORDER_LINE: {
      // positionType 기반으로 색상 재계산 (저장 시 stroke를 포함하지 않으므로)
      const posColor =
        shape.positionType === 'long' ? COLORS.long : shape.positionType === 'short' ? COLORS.short : COLORS.hedge;

      // storage의 price/positionType/tpslPrice/coordinates를 제외한 나머지 필드를 복사한 뒤
      // runtime 필드(yValue, positionType, tpslPrice 배열, 스타일 기본값)를 주입한다.
      const {
        price: _price,
        positionType: _posType,
        tpslPrice: _tpsl,
        coordinates: _coords,
        ...rest
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      } = shape as unknown as Record<string, unknown>;
      void _price;
      void _posType;
      void _tpsl;
      void _coords;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return {
        ...rest,
        type: ShapeType.ORDER_LINE,
        ...ORDER_LINE_RENDER_DEFAULTS,
        stroke: posColor,
        textFill: posColor,
        edge: { ...ORDER_LINE_RENDER_DEFAULTS.edge, stroke: posColor },
        yValue: shape.price,
        positionType: shape.positionType,
        amount: shape.amount,
        tpslPrice: shape.tpsl.map(({ tp, sl }) => ({
          tp:
            tp !== 0
              ? netRoeToPrice({
                  netRoe: tp,
                  entry: shape.price,
                  amount: shape.amount,
                  positionType: shape.positionType,
                })
              : 0,
          sl:
            sl !== 0
              ? netRoeToPrice({
                  netRoe: sl,
                  entry: shape.price,
                  amount: shape.amount,
                  positionType: shape.positionType,
                })
              : 0,
        })),
        tpsl: shape.tpsl.map(({ tp, sl }) => ({ tp, sl })),
        isEntered: false,
        ...runtime,
      } as unknown as OrderLineRenderNode;
    }

    default: {
      // StorageShape는 MEASURE를 포함하지 않으므로 exhaustive
      const _exhaustive: never = shape;
      throw new Error(`Unknown StorageShape type: ${(_exhaustive as { type: string }).type}`);
    }
  }
}

// ─── RenderNode → StorageShape ──────────────────────────────────────

/**
 * RenderNode → StorageShape 변환.
 *
 * `storageToRenderNode`의 역방향. localStorage 저장 시 호출한다.
 * RenderNode의 도형별 좌표 mixin 필드를 `coordinates: Coordinate[]` 배열로 재조립하고,
 * runtime 전용 필드(hidden, locked, node)를 제거한다. 속성 필드는 spread로 그대로 복사된다.
 *
 * 주의: 이 함수는 좌표를 **재조립만** 하며 timestamp↔index 변환은 수행하지 않는다.
 * index-based 좌표를 실제 timestamp로 변환하려면 호출부에서 `shapeToCoordinates`를 별도로 사용한 뒤
 * 그 결과로 `coordinates`를 덮어써야 한다.
 *
 * Measure 도형은 저장 비대상이므로 이 함수에 전달하면 안 된다.
 */
export function nodeToStorageShape(node: RenderNode): StorageShapeInput {
  // runtime 전용 필드 제거.
  // - node: RFC 라이브러리 node 참조
  // - selected: 런타임 UI 상태 (선택 여부)
  // - barsRange/daysBetween/priceRange/totalPercentChange/angle: TrendLine handleEnd가 그리는 시점에 좌표로부터 계산하는 derived 필드. 저장 후 candle 데이터가 바뀌면 stale이 되므로 persist하지 않는다 (runtime에서 재계산)
  //
  // 보존 필드 (runtime 전용이 아닌 사용자 설정):
  // - hidden/locked: 사용자가 명시적으로 설정한 영구 상태. BaseStorageShape에 정의되어 있음.
  const {
    node: _rfcNode,
    selected: _selected,
    barsRange: _barsRange,
    daysBetween: _daysBetween,
    priceRange: _priceRange,
    totalPercentChange: _totalPercentChange,
    angle: _angle,
    ...rest
  } = node as RenderNode & {
    node?: unknown;
    selected?: unknown;
    barsRange?: unknown;
    daysBetween?: unknown;
    priceRange?: unknown;
    totalPercentChange?: unknown;
    angle?: unknown;
  };
  void _rfcNode;
  void _selected;
  void _barsRange;
  void _daysBetween;
  void _priceRange;
  void _totalPercentChange;
  void _angle;

  // prepareNewShape / updateCoordinate가 저장해둔 timestamp 기반 coordinates.
  // 이 값이 있으면 인덱스 기반 좌표 필드(start/end 등)를 재조립하지 않고 그대로 사용한다.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const savedCoordinates = (node as unknown as { coordinates?: Coordinate[] }).coordinates;

  switch (node.type) {
    case ShapeType.TREND_LINE:
    case ShapeType.RAY:
    case ShapeType.INFO_LINE: {
      const coordinates =
        savedCoordinates && savedCoordinates.length >= 2 ? savedCoordinates : assembleStartEnd(node.start, node.end);
      const { start: _s, end: _e, ...r } = rest as Record<string, unknown>;
      void _s;
      void _e;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.HORZ_LINE: {
      const yValue = node.yValue ?? 0;
      const coordinates: Coordinate[] = [[0, yValue]];
      const { yValue: _y, ...r } = rest as Record<string, unknown>;
      void _y;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.VERT_LINE: {
      const coordinates: Coordinate[] =
        savedCoordinates && savedCoordinates.length >= 1 ? savedCoordinates : [[node.xValue ?? 0, 0]];
      const { start: _s, end: _e, xValue: _x, ...r } = rest as Record<string, unknown>;
      void _s;
      void _e;
      void _x;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.PARALLEL_CHANNEL:
    case ShapeType.TRIANGLE:
    case ShapeType.FIB_CHANNEL:
    case ShapeType.GANN_BOX:
    case ShapeType.GANN_FAN: {
      const coordinates =
        savedCoordinates && savedCoordinates.length >= 2
          ? savedCoordinates
          : assembleThreePoint(node.startXY, node.endXY, node.endXY2);
      const { startXY: _sx, endXY: _ex, endXY2: _ex2, dy: _dy, ...r } = rest as Record<string, unknown>;
      void _sx;
      void _ex;
      void _ex2;
      void _dy;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.FIB_RETRACEMENT: {
      const coordinates: Coordinate[] =
        savedCoordinates && savedCoordinates.length >= 2
          ? savedCoordinates
          : node.x1 !== undefined && node.y1 !== undefined && node.x2 !== undefined && node.y2 !== undefined
            ? [
                [node.x1, node.y1],
                [node.x2, node.y2],
              ]
            : [];
      const { x1: _x1, y1: _y1, x2: _x2, y2: _y2, ...r } = rest as Record<string, unknown>;
      void _x1;
      void _y1;
      void _x2;
      void _y2;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.ELLIOTT_IMPULSE_12345:
    case ShapeType.ELLIOTT_CORRECTION_ABC:
    case ShapeType.XABCD_PATTERN:
    case ShapeType.PATH: {
      const coordinates: Coordinate[] =
        savedCoordinates && savedCoordinates.length >= 2
          ? savedCoordinates
          : (node.points ?? []).map(p => [p[0], p[1]] as Coordinate);
      const { points: _p, ...r } = rest as Record<string, unknown>;
      void _p;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.RECTANGLE:
    case ShapeType.FIXED_RANGE_VOLUME_PROFILE: {
      const coordinates =
        savedCoordinates && savedCoordinates.length >= 2 ? savedCoordinates : assembleStartEnd(node.start, node.end);
      const { start: _s, end: _e, ...r } = rest as Record<string, unknown>;
      void _s;
      void _e;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.TEXT: {
      const coordinates: Coordinate[] =
        savedCoordinates && savedCoordinates.length >= 1
          ? savedCoordinates
          : node.position
            ? [[node.position[0], node.position[1]]]
            : [];
      const { position: _pos, ...r } = rest as Record<string, unknown>;
      void _pos;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.ANCHORED_VOLUME_PROFILE: {
      const coordinates: Coordinate[] =
        savedCoordinates && savedCoordinates.length >= 1
          ? savedCoordinates
          : node.start
            ? [[node.start[0], node.start[1]]]
            : [];
      const { start: _s, ...r } = rest as Record<string, unknown>;
      void _s;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
      return { ...r, coordinates } as StorageShapeInput;
    }

    case ShapeType.ORDER_LINE: {
      // Runtime → storage 복원:
      //   price       ← yValue
      //   positionType ← runtime broad → storage narrow ('long' | 'short')
      //   coordinates ← [] (OrderLine은 좌표를 사용하지 않음)
      //   ORDER_LINE_RENDER_DEFAULTS / edge / stroke / textFill 등
      //   runtime 전용 스타일 필드는 제거
      const olNode = node;
      const narrowPos: 'long' | 'short' = olNode.positionType === 'short' ? 'short' : 'long';
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return {
        uuid: olNode.uuid,
        ...(olNode.id !== undefined ? { id: olNode.id } : {}),
        type: ShapeType.ORDER_LINE,
        chartId: olNode.chartId,
        createdAt: olNode.createdAt,
        updatedAt: olNode.updatedAt,
        order: olNode.order,
        coordinates: [],
        price: olNode.yValue ?? 0,
        positionType: narrowPos,
        amount: olNode.amount ?? 0,
        tpsl: Array.isArray(olNode.tpsl) ? olNode.tpsl : [],
      } as unknown as StorageShapeInput;
    }

    case ShapeType.MEASURE:
      // 저장 비대상 — 이 함수에 도달하면 안 됨 (호출부에서 사전 필터).
      throw new Error(`Non-storable shape type passed to nodeToStorageShape: ${node.type}`);

    default: {
      const _exhaustive: never = node;
      throw new Error(`Unknown ShapeType: ${(_exhaustive as { type: string }).type}`);
    }
  }
}

// ─── 내부 좌표 조립 헬퍼 ────────────────────────────────────────────

function assembleStartEnd(start: [number, number] | undefined, end: [number, number] | undefined): Coordinate[] {
  if (start && end) {
    return [
      [start[0], start[1]],
      [end[0], end[1]],
    ];
  }
  return [];
}

function assembleThreePoint(
  startXY: [number, number] | undefined,
  endXY: [number, number] | undefined,
  endXY2: [number, number] | undefined,
): Coordinate[] {
  if (!startXY || !endXY) return [];
  const coords: Coordinate[] = [
    [startXY[0], startXY[1]],
    [endXY[0], endXY[1]],
  ];
  if (endXY2) {
    coords.push([endXY2[0], endXY2[1]]);
  }
  return coords;
}
